/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import _ from 'lodash';
import prettyBytes from 'pretty-bytes';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache, useQuery } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import RestoreIcon from '@mui/icons-material/Restore';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { Badge, Box, Divider, Grid, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';

import { CIColors, ClusterStates, Endpoints, Infer, KubernetesIdentifier, ServiceTypeMatrix } from 'shared';

import { UseUpdateClusterData, UseUpdateClusterParams } from '../../../../components/hooks/useUpdateCluster';
import { ServiceIcon } from '../../../../components/ServiceIcon';
import { SimpleConfirmModal } from '../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { BasicRoutes } from '../../../../constants/basicRoutes';
import { UnitsToBytesMultiplier } from '../../../../elements/HUBytesInput/components/UnitPicker.types';
import { HUChip } from '../../../../elements/HUChip/HUChip';
import { Namespaces } from '../../../../i18n';
import { ClusterApiService } from '../../../../services/ClusterApiService';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { ApiClientResponse } from '../../../../types/api/ApiClientResponse';
import { EntityId } from '../../../../types/api/EntityId';
import { ClusterResponsePayload } from '../../../../types/cluster/ClusterResponsePayload';
import { ClusterStorageResponsePayload } from '../../../../types/cluster/ClusterStorageResponsePayload';
import { ClusterUpdateRequestBody } from '../../../../types/cluster/ClusterUpdateRequestBody';
import { UpdateClusterDetailsParameterIdentification } from '../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { IProject } from '../../../../types/project/IProject';
import { determineStatusColor } from '../../../../utils/determineStatusColor';
import { prepareRoutePath } from '../../../../utils/prepareRoutePath';
import {
  fetchClusterUpgradeStatus,
  fetchContinuousBackupLatestRestorePoint,
  fetchContinuousBackupOldestRestorePoint as fetchRestorePoint,
  fetchPostgresVersions,
} from '../../../../utils/queryFns';
import { reactToUpdateCluster } from '../../../../utils/reactions';
import { clusterUpgradeHandled } from '../../../../utils/storageHelpers';
import { useControlOverlays } from '../../../../utils/useControlOverlays';
import { useFavoriteServices } from '../../../../utils/useFavoriteServices';
import { useObjectState } from '../../../../utils/useObjectState';
import { useProjectPermissions } from '../../../../utils/useProjectPermissions';
import { EditableTypography } from '../../../ManageOrganizationPage/components/TeamsTab/EditableTypography';
import { EditClusterDialog } from '../EditClusterDialog/EditClusterDialog';
import { RestoreClusterDialog } from '../RestoreClusterDialog/RestoreClusterDialog';
import { UpgradeClusterDialog } from '../UpgradeClusterDialog/UpgradeClusterDialog';
import { createClusterDetailsHeaderStyles } from './ClusterDetailsHeader.styles';
import { ServiceStateControlChip } from './components/ServiceStateControlChip/ServiceStateControlChip';

type Props = {
  service: Infer<typeof Endpoints.GetCluster>['Response']['200'];
  status: ClusterStates | undefined;
  storage: ClusterStorageResponsePayload;
  isLoadingDetails: boolean;
  maintenanceWindows: string[] | null;
  updateData: (
    data: UseUpdateClusterData['data'],
    params?: UseUpdateClusterParams,
    throwOnError?: boolean,
  ) => Promise<ApiClientResponse<ClusterResponsePayload> | undefined>;
  desiredReplicas: number | null;
  identificationData: UpdateClusterDetailsParameterIdentification;
  deleteCluster: () => any | Promise<any>;
  relativeProjects: IProject[];
  isSpinning: boolean;
  ids: {
    cluster: string;
    project: string;
    organization?: string;
  };
};

type ModalState = {
  deleteModal: { isOpen: boolean };
  editModal: { isOpen: boolean };
  upgradeModal: { isOpen: boolean };
  restoreModal: { isOpen: boolean };
  errorModal: { isOpen: boolean; message: string };
  errorUpgradeModal: { isOpen: boolean };
};

export const ClusterDetailsHeader = memo<Props>(props => {
  const {
    service,
    status,
    storage,
    isLoadingDetails,
    maintenanceWindows,
    updateData,
    desiredReplicas,
    deleteCluster,
    identificationData,
    relativeProjects,
    isSpinning,
  } = props;

  const { t } = useTranslation([Namespaces.Cluster, Namespaces.Common]);
  const classes = createClusterDetailsHeaderStyles();
  const navigate = useNavigate();
  const deleteFlag = useRef<null | EntityId>(null);
  const editLocationFlag = useRef<EntityId | null>(null);

  const restorePointQuery = useQuery([fetchRestorePoint.key, identificationData], fetchRestorePoint.fn, {
    retry: true,
  });
  const oldestRestorePoint = useMemo(() => restorePointQuery.data?.payload.lastModified, [restorePointQuery.data]);

  const currentPlan = useMemo(() => service?.configuration?.filter(conf => conf.endedAt === 'infinity')[0], [service]);
  const params = useParams<{
    organization_id: string;
    project_id: string;
    cluster_id: string;
  }>();
  const { favoriteServices, toggleFavorite } = useFavoriteServices();

  const organizationId = params.organization_id!;
  const projectId = params.project_id!;
  const clusterId = params.cluster_id!;

  const permissions = useProjectPermissions(organizationId, projectId);

  const [isUpgradeHandled, setIsUpgradeHandled] = useState(() => clusterUpgradeHandled.get(clusterId));

  useEffect(() => {
    setIsUpgradeHandled(clusterUpgradeHandled.get(clusterId));
  }, [clusterId]);

  const postgresVersionsQuery = useQuery([fetchPostgresVersions.key], fetchPostgresVersions.fn);

  const postgresVersions = useMemo(
    () => postgresVersionsQuery.data?.payload.versions ?? [],
    [postgresVersionsQuery.data],
  );

  const isUpgradeAvailable = useMemo(
    () => currentPlan && postgresVersions.includes(currentPlan.postgresVersion + 1),
    [postgresVersions, currentPlan],
  );

  const clusterUpgradeStatusQuery = useQuery(
    [fetchClusterUpgradeStatus.key, { ...identificationData }],
    fetchClusterUpgradeStatus.fn,
    {
      retry: true,
      refetchInterval: 5_000,
      enabled: isUpgradeAvailable,
    },
  );

  const clusterUpgradeStatus = useMemo(() => clusterUpgradeStatusQuery.data?.payload, [clusterUpgradeStatusQuery.data]);

  const latestRestorePointQuery = useQuery(
    [
      fetchContinuousBackupLatestRestorePoint.key,
      {
        ...identificationData,
      },
    ],
    fetchContinuousBackupLatestRestorePoint.fn,
    { retry: false },
  );

  const latestRestorePoint = useMemo(
    () => latestRestorePointQuery.data?.payload || null,
    [latestRestorePointQuery.data],
  );

  const [modalState, setModalState] = useObjectState<ModalState>({
    deleteModal: { isOpen: false },
    editModal: { isOpen: false },
    upgradeModal: { isOpen: false },
    restoreModal: { isOpen: false },
    errorModal: { isOpen: false, message: '' },
    errorUpgradeModal: { isOpen: false },
  });

  useEffect(() => {
    if (clusterUpgradeStatus && clusterUpgradeStatus.status === 'success' && !isUpgradeHandled) {
      void reactToUpdateCluster(identificationData);
      clusterUpgradeHandled.set(clusterId, true);
      setIsUpgradeHandled(true);
    }
  }, [clusterUpgradeStatus, identificationData, isUpgradeHandled, clusterId]);

  useEffect(() => {
    deleteFlag.current = null;
    editLocationFlag.current = null;
    return () => {
      deleteFlag.current = null;
      editLocationFlag.current = null;
    };
  }, [organizationId, projectId, clusterId]);

  const { doOpenCreateClusterOverlay } = useControlOverlays();
  const isFavorite = favoriteServices.some(fv => fv.id === service.id);

  const HorizontalDivider = <Divider classes={{ root: classes.divider }} />;

  const cloneService = useCallback(
    () =>
      doOpenCreateClusterOverlay({
        fromBackup: true,
        serviceType: service.type,
        organizationId,
        projectId,
        clusterId,
      }),
    [service.type, clusterId, doOpenCreateClusterOverlay, organizationId, projectId],
  );

  const onEditCluster = async (data: ClusterUpdateRequestBody) => {
    try {
      const { organizationId, projectId, clusterId } = identificationData;

      await updateData(data, { skipReaction: true }, true);
      const newProjectId = data.metadata?.projectId;
      if (newProjectId && projectId !== newProjectId) {
        editLocationFlag.current = newProjectId;
        queryCache.removeQueries(
          ({ queryKey }) =>
            _.get(queryKey[1], 'clusterId') === clusterId && _.get(queryKey[1], 'projectId') === projectId,
        );
        await reactToUpdateCluster({ ...identificationData, projectId: newProjectId });
        void navigate(
          prepareRoutePath(BasicRoutes.CLUSTER_PAGE, {
            ':organization_id': organizationId,
            ':project_id': newProjectId,
            ':cluster_id': clusterId,
          }),
        );
      } else {
        void reactToUpdateCluster({ ...identificationData });
      }
    } catch (e) {
      const error = e as ApiClientError;
      setModalState({
        errorModal: {
          isOpen: true,
          message: error.response?.data.message ?? '',
        },
      });
    }
  };

  const onSubmit = (values: { name?: string; description?: string | null }) => {
    const data: {
      name?: string;
      description?: string | null;
    } = {};
    if (values.name !== undefined) {
      data.name = values.name?.trim();
    }
    if (values.description !== undefined) {
      data.description = values.description?.trim() || null;
    }
    void onEditCluster({ metadata: data });
  };

  const allowedClusterEdit = status !== ClusterStates.Creating;
  const moveButtonTooltip = useMemo(
    () => (!allowedClusterEdit ? t('details.header.move_not_available') : t('details.header.name.move')),
    [allowedClusterEdit, t],
  );

  const isPostgres = useMemo(() => service.type !== 'cypex' && service.type !== 'migrator', [service.type]);

  const isUpgradeDisabled = useMemo(
    () => !allowedClusterEdit || isSpinning || clusterUpgradeStatus?.status === 'accepted' || isLoadingDetails,
    [allowedClusterEdit, isSpinning, clusterUpgradeStatus, isLoadingDetails],
  );

  const upgradeTooltip = useMemo(
    () =>
      clusterUpgradeStatus?.status === 'accepted'
        ? clusterUpgradeStatus?.isScheduled
          ? t('details.header.upgrade_scheduled_tooltip')
          : t('details.header.upgrade_in_progress_tooltip')
        : !allowedClusterEdit || isSpinning || isLoadingDetails
          ? ''
          : t('details.header.upgrade_tooltip'),
    [clusterUpgradeStatus, allowedClusterEdit, isSpinning, isLoadingDetails, t],
  );

  const badgeColor = useMemo(
    () => (clusterUpgradeStatus?.status === 'accepted' ? CIColors.Tertiary : CIColors.Warning),
    [clusterUpgradeStatus],
  );

  const onUpgrade = useCallback(
    async (schedule: boolean = false) => {
      try {
        await ClusterApiService.upgradeClusterPostgresVersion({ ...identificationData, schedule });
        clusterUpgradeHandled.set(clusterId, false);
        setIsUpgradeHandled(false);
        setModalState({ upgradeModal: { isOpen: false } });
      } catch (e) {
        const error = e as ApiClientError;
        console.error(error);
        setModalState({ errorUpgradeModal: { isOpen: true } });
      }
    },
    [identificationData, setModalState, setIsUpgradeHandled, clusterId],
  );

  return (
    <div className={classes.panel}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" width="100%">
          <Box
            sx={{
              alignItems: 'center',
              '& img': {
                paddingRight: '8px',
                height: '64px',
                width: 'auto',
              },
            }}
          >
            {service.type ? (
              <ServiceIcon variant="minimal" serviceType={service.type} />
            ) : (
              <Skeleton variant="circular" sx={{ width: '64px', height: '64px', mr: '8px' }} />
            )}
          </Box>
          <Stack direction="column" gap={1} width="100%">
            {service.name ? (
              <>
                <EditableTypography
                  className={classes.panelTitle}
                  readOnly={!permissions.edit_cluster}
                  value={service.name}
                  schema={Yup.string()
                    .trim()
                    .min(
                      KubernetesIdentifier.MinLength,
                      t('validation:rules.min_length', {
                        field: t(`general:description_panel.form.name`),
                        minLength: KubernetesIdentifier.MinLength,
                      }),
                    )
                    .max(
                      KubernetesIdentifier.MaxLength,
                      t('validation:rules.max_length', {
                        field: t(`general:description_panel.form.name`),
                        maxLength: KubernetesIdentifier.MaxLength,
                      }),
                    )
                    .matches(KubernetesIdentifier.Regex, t('validation:rules.invalid'))
                    .required(t('validation:rules.empty', { field: t(`general:description_panel.form.name`) }))}
                  onSubmit={async value => value && onSubmit({ name: value })}
                  data-cy="description-panel-name-input"
                />
                <EditableTypography
                  readOnly={!permissions.edit_cluster}
                  value={service.description}
                  placeholder={t(`general:description_panel.form.description_placeholder`)}
                  schema={Yup.string()
                    .trim()
                    .nullable()
                    .max(
                      200,
                      t('validation:rules.max_length', {
                        field: t(`general:description_panel.form.description`),
                        maxLength: 200,
                      }),
                    )}
                  onSubmit={async value => onSubmit({ description: value })}
                />
              </>
            ) : (
              <>
                <Skeleton variant="rounded" height={23} sx={{ mr: '8px' }} />
                <Skeleton variant="rounded" height={23} sx={{ mr: '8px' }} />
              </>
            )}
          </Stack>
        </Stack>
        <div className={classes.panelActionsWrapper}>
          {ServiceTypeMatrix.sourceClusterForCreatingPostgresqlFromBackup[service.type] &&
            permissions.create_cluster && (
              <Tooltip title={t('details.header.create_from_backup')}>
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={cloneService}
                  size="small"
                  data-cy="cluster-page-clone-cluster-button"
                >
                  <FileCopyIcon />
                </IconButton>
              </Tooltip>
            )}
          {ServiceTypeMatrix.sourceClusterForCreatingPostgresqlFromBackup[service.type] && permissions.edit_cluster && (
            <Tooltip title={t('details.header.restore_from_backup')}>
              <IconButton
                sx={{ padding: 0 }}
                onClick={() => setModalState({ restoreModal: { isOpen: true } })}
                size="small"
                disabled={!allowedClusterEdit || !status || !oldestRestorePoint}
                data-cy="cluster-page-restore-cluster-button"
              >
                <RestoreIcon />
              </IconButton>
            </Tooltip>
          )}
          {permissions.edit_cluster && (
            <Tooltip title={moveButtonTooltip}>
              <IconButton
                sx={{ padding: 0 }}
                onClick={() => setModalState({ editModal: { isOpen: true } })}
                size="small"
                disabled={!allowedClusterEdit || !status}
                data-cy="cluster-page-move-to-button"
              >
                <MoveDownIcon />
              </IconButton>
            </Tooltip>
          )}
          {permissions.delete_cluster && (
            <Tooltip
              title={
                service.isCloning
                  ? t('details.header.delete_disable_reason.being_cloned_already')!
                  : t('details.header.delete')!
              }
            >
              <IconButton
                sx={{ padding: 0 }}
                onClick={() => setModalState({ deleteModal: { isOpen: true } })}
                size="small"
                disabled={service.isCloning || !status}
                data-cy="cluster-page-delete-cluster-button"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isFavorite ? t('details.header.favorite.remove')! : t('details.header.favorite.add')!}>
            <IconButton
              sx={{ padding: 0 }}
              onClick={e => toggleFavorite(e, service.id)}
              size="small"
              disabled={!status}
              data-cy={`cluster-page-favorite-cluster-button-${isFavorite}`}
            >
              {isFavorite ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.panelStatusWrapper}>
          {status ? (
            ServiceTypeMatrix.manageClusterLifecycle[service.type] ? (
              <ServiceStateControlChip status={status} identificationData={identificationData} />
            ) : (
              <HUChip
                color={determineStatusColor(status)}
                className={classes.chipStatus}
                label={t(`${Namespaces.Entities}:cluster.k8s_status.values.${status.toLowerCase()}`)}
                data-cy="cluster-header-status"
              />
            )
          ) : (
            <Skeleton
              variant="rounded"
              width={82}
              height={34}
              sx={{ borderRadius: '16px' }}
              className={classes.chipStatus}
            />
          )}
          <Tooltip title={t('details.header.raiseTicket')!}>
            <IconButton
              target="_blank"
              rel="noreferrer"
              component={Link}
              className={classes.raiseRequestIcon}
              to={prepareRoutePath(
                BasicRoutes.SUPPORT,
                {},
                Object.entries(props.ids)
                  .filter(([, value]) => value)
                  .map(([key, value]) => `${key}=${value}`)
                  .join('&'),
              )}
              size="small"
              disabled={!status}
            >
              <ContactSupportIcon />
            </IconButton>
          </Tooltip>
        </div>
      </Stack>
      <Grid container wrap="nowrap" classes={{ root: classes.subHeaderWrapper }}>
        {isPostgres && (
          <Grid item classes={{ root: classes.subHeaderItem }}>
            <Typography variant="caption" className={classes.subHeaderLabel}>
              {t('details.header.replica')}
            </Typography>
            <Grid>{HorizontalDivider}</Grid>
            {currentPlan ? (
              <Typography data-cy="cluster-header-replica" variant="body1" className={classes.subHeaderValue}>
                {currentPlan.replicas + 1}
              </Typography>
            ) : (
              <Skeleton />
            )}
          </Grid>
        )}
        {desiredReplicas !== null && desiredReplicas !== currentPlan.replicas && (
          <Grid item classes={{ root: classes.subHeaderItem }}>
            <Typography variant="caption" className={classes.subHeaderLabel}>
              {t('details.header.desired_replica')}
            </Typography>
            <Grid>{HorizontalDivider}</Grid>
            <Typography variant="body1" className={classes.subHeaderValue}>
              {desiredReplicas}
            </Typography>
          </Grid>
        )}
        <Grid item classes={{ root: classes.subHeaderItem }}>
          <Typography variant="caption" className={classes.subHeaderLabel}>
            {t('create.plan.cpu')}
          </Typography>
          <Grid>{HorizontalDivider}</Grid>
          {currentPlan ? (
            <Typography className={classes.subHeaderValue}>{currentPlan.cpuUnits ?? 0}</Typography>
          ) : (
            <Skeleton />
          )}
        </Grid>
        <Grid item classes={{ root: classes.subHeaderItem }}>
          <Typography variant="caption" className={classes.subHeaderLabel}>
            RAM
          </Typography>
          <Grid>{HorizontalDivider}</Grid>
          {currentPlan ? (
            <Typography className={classes.subHeaderValue}>
              {prettyBytes(currentPlan.memoryGbs * UnitsToBytesMultiplier['GB'])}
            </Typography>
          ) : (
            <Skeleton />
          )}
        </Grid>
        <Grid item classes={{ root: classes.subHeaderItem }}>
          <Typography variant="caption" className={classes.subHeaderLabel}>
            {t('details.header.storage')}
          </Typography>
          <Grid>{HorizontalDivider}</Grid>
          {currentPlan ? (
            <Typography className={classes.subHeaderValue}>
              {prettyBytes(currentPlan.storageGbs * UnitsToBytesMultiplier['GB'])}
            </Typography>
          ) : (
            <Skeleton />
          )}
        </Grid>
        <Grid item classes={{ root: classes.subHeaderItem }}>
          <Typography variant="caption" className={classes.subHeaderLabel}>
            {t('details.header.storage_used')}
          </Typography>
          <Grid>{HorizontalDivider}</Grid>
          {storage.usedPercent ? (
            <Typography className={classes.subHeaderValue}>{storage.usedPercent}</Typography>
          ) : (
            <Skeleton />
          )}
        </Grid>
        {isPostgres && (
          <Grid item classes={{ root: classes.subHeaderItem }}>
            <Badge
              invisible={!(permissions.edit_cluster && isUpgradeAvailable)}
              componentsProps={{
                badge: { style: { paddingTop: '5px', paddingRight: '10px' } },
              }}
              badgeContent={
                <Tooltip data-cy="cluster-header-upgrade-postgres-version-tooltip" arrow title={upgradeTooltip}>
                  <Badge
                    componentsProps={{ badge: { style: { backgroundColor: badgeColor } } }}
                    variant="dot"
                    overlap="circular"
                  >
                    <IconButton
                      sx={{ padding: 0 }}
                      onClick={() => setModalState({ upgradeModal: { isOpen: true } })}
                      size="small"
                      disabled={isUpgradeDisabled}
                      data-cy="cluster-header-upgrade-postgres-version-button"
                    >
                      <UpgradeIcon />
                    </IconButton>
                  </Badge>
                </Tooltip>
              }
            >
              <Typography variant="caption" className={classes.subHeaderLabel}>
                {t(`${Namespaces.Admin}:postgres_version.postgresql`)}
              </Typography>
            </Badge>
            <Grid>{HorizontalDivider}</Grid>
            {currentPlan ? (
              <Typography data-cy="cluster-header-postgres-version" className={classes.subHeaderValue}>
                {currentPlan?.postgresVersion}
              </Typography>
            ) : (
              <Skeleton />
            )}
          </Grid>
        )}
      </Grid>
      <EditClusterDialog
        open={modalState.editModal.isOpen}
        editCluster={onEditCluster}
        identificationData={identificationData}
        relativeProjects={relativeProjects}
        onCancel={() => setModalState({ editModal: { isOpen: false } })}
      />
      {currentPlan && (
        <UpgradeClusterDialog
          open={modalState.upgradeModal.isOpen}
          currentVersion={currentPlan.postgresVersion}
          latestBackupTimestamp={latestRestorePoint?.lastModified || null}
          maintenanceWindows={maintenanceWindows}
          onUpgrade={onUpgrade}
          onCancel={() => setModalState({ upgradeModal: { isOpen: false } })}
        />
      )}
      <RestoreClusterDialog
        open={modalState.restoreModal.isOpen}
        onCancel={() => setModalState({ restoreModal: { isOpen: false } })}
        identificationData={identificationData}
        storageAvailable={storage.available}
        oldestRestorePoint={oldestRestorePoint}
      />
      <SimpleConfirmModal
        open={modalState.deleteModal.isOpen}
        message={t(`${Namespaces.Common}:confirmation.delete_cluster`, {
          name: service.name,
        })}
        onCancel={() => setModalState({ deleteModal: { isOpen: false } })}
        onConfirm={() => {
          setModalState({ deleteModal: { isOpen: false } });
          deleteCluster();
        }}
      />
      <SimpleErrorModal
        open={modalState.errorModal.isOpen}
        message={`${Namespaces.Common}:update_cluster_errors.${modalState.errorModal.message}`}
        onOk={() => setModalState({ errorModal: { isOpen: false, message: '' } })}
      />
      <SimpleErrorModal
        open={modalState.errorUpgradeModal.isOpen}
        message={t(`${Namespaces.Common}:errors.unexpected_error`)}
        onOk={() => setModalState({ errorUpgradeModal: { isOpen: false }, upgradeModal: { isOpen: false } })}
      />
    </div>
  );
});
